import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { useSelector } from 'react-redux'
import { RootState } from '../rootState'
import { ReactElement } from 'react'
import { User } from '../../types/User'
import { Memo } from '../../types/Memo'

const initialState = {
  memos: [],
  memoElements: [],
  triggerToInsert: 0,
  finalMemos: [],
  title: '',
  link: '',
  user: {
    id: '',
    photoUrl: '',
    name: '',
  },
  memoId: '',
  heads: 'other',
}

interface MemoState {
  memos: Memo[]
  memoElements: ReactElement[]
  triggerToInsert: number
  finalMemos: MemoElement[]
  title: string
  link: string
  user: User
  memoId: string
  heads: string
}

export interface MemoElement {
  id: string
  korean: string
  other: string
  index?: number
}

const slice = createSlice({
  name: 'memo',
  initialState,
  reducers: {
    setMemos: (state: MemoState, action: PayloadAction<Memo[]>) => {
      state.memos = action.payload
    },
    append: (state: MemoState, action: PayloadAction<ReactElement>) => {
      state.memoElements = [...state.memoElements, action.payload]
    },
    removeReactElement: (state: MemoState, action: PayloadAction<string>) => {
      const element = state.memoElements.find((e) => e.key === action.payload)
      if (!element) {
        return
      }
      state.memoElements = state.memoElements.filter(
        (e) => e.key !== element.key
      )
    },
    clearReactElement: (state: MemoState) => {
      state.memoElements = []
    },
    swap: (
      state: MemoState,
      action: PayloadAction<{ from: string; to: string }>
    ) => {
      const fromElement = state.memoElements.find(
        (e) => e.key === action.payload.from
      )
      const toElement = state.memoElements.find(
        (e) => e.key === action.payload.to
      )

      if (!fromElement || !toElement) {
        return
      }

      const fromIndex = state.memoElements.findIndex(
        (e) => e.key === fromElement.key
      )
      const toIndex = state.memoElements.findIndex(
        (e) => e.key === toElement.key
      )

      const copiedArray = [...state.memoElements]
      copiedArray[toIndex] = fromElement
      copiedArray[fromIndex] = toElement

      state.memoElements = copiedArray
    },
    triggerToInsert: (
      state: MemoState,
      action: PayloadAction<{
        triggerToInsert: number
        title: string
        link: string
        user: User
        heads: string
      }>
    ) => {
      state.triggerToInsert = action.payload.triggerToInsert
      state.title = action.payload.title
      state.link = action.payload.link
      state.user = action.payload.user
    },
    triggerToUpdate: (
      state: MemoState,
      action: PayloadAction<{
        triggerToInsert: number
        title: string
        link: string
        user: User
        memoId: string
        heads: string
      }>
    ) => {
      state.triggerToInsert = action.payload.triggerToInsert
      state.title = action.payload.title
      state.link = action.payload.link
      state.user = action.payload.user
      state.memoId = action.payload.memoId
    },
    resetUpdateData: (state: MemoState) => {
      state.title = ''
      state.link = ''
      state.user = {
        id: '',
        photoUrl: '',
        name: '',
      }
      state.memoId = ''
    },
    appendFinalMemo: (state: MemoState, action: PayloadAction<MemoElement>) => {
      const found = state.finalMemos.find((e) => e.id === action.payload.id)
      if (found) {
        return
      }
      state.finalMemos = [...state.finalMemos, action.payload]
    },
    removeFinalMemo: (state: MemoState, action: PayloadAction<string>) => {
      state.finalMemos = state.finalMemos.filter((e) => e.id !== action.payload)
    },
    clearFinalMemo: (state: MemoState) => {
      state.finalMemos = []
    },
    remove: (state: MemoState, action: PayloadAction<string>) => {
      state.finalMemos = state.finalMemos.filter((e) => e.id !== action.payload)
    },
  },
})

export const useMemos = (): Memo[] =>
  useSelector((state: RootState) => state.memo.memos)

export const useMemoElements = (): ReactElement[] =>
  useSelector((state: RootState) => state.memo.memoElements)

export const useTriggerToInsert = (): number =>
  useSelector((state: RootState) => state.memo.triggerToInsert)

export const MEMO_STORE_NAME = slice.name
export const MemosActions = slice.actions
export default slice.reducer
